import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

import { ContentDocumentLink } from 'src/app/models/content-document-link.model';
import { LineItem } from 'src/app/models/line-item.model';
import { Order, OrderDetailViews as views } from 'src/app/models/order.model';
import {
  AttachmentService,
  FileAPI,
} from 'src/app/core/services/attachment';
import * as fromSharedComponents from 'src/app/shared/components';
import * as fromStore from 'src/app/store/';
import { OrderState } from 'src/app/store/reducers/orders.reducer';
import { OrdersModule } from '../../orders.module';

@Component({
  selector: 'app-orders-detail-shell',
  templateUrl: './detail-shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AsyncPipe, OrdersModule],
})
export class OrdersDetailShellComponent implements OnInit {
  @Input() detailView: string;

  private cdr = inject(ChangeDetectorRef);

  private orderStore = inject(Store<OrderState>);

  private store = inject(Store<fromStore.State>);

  private attachmentService = inject(AttachmentService);

  private dialog = inject(MatDialog);

  private route = inject(ActivatedRoute);

  private router = inject(Router);

  private readonly destroyRef = inject(DestroyRef);

  iconDetail = 'assignment';

  orders$: Observable<Order[]> = this.orderStore.select(fromStore.getAllOrders);

  isLoading$: Observable<boolean> = this.orderStore.select(
    fromStore.getOrdersLoading,
  );

  order$: Observable<Order>;

  readonly order = signal<Order | null>(null);

  lineItem$: Observable<LineItem>;

  activeOrders$: Observable<Order[]>;

  totalActiveLocations$: Observable<number>;

  serviceOrderForm$: Observable<ContentDocumentLink>;

  readonly formAPIData = signal<FileAPI | null>(null);

  browserSize$: Observable<string> = this.store.select(
    fromStore.getBrowserSizeName,
  );

  selectedView = views;

  isOrderIdOnRouter = false;

  isLineItemIdOnRouter = false;

  activeOrderSubscription: Subscription;

  orderResCurr: any;

  readonly isSOFLoading = signal<boolean>(true);

  readonly browserSize = toSignal(this.browserSize$, { initialValue: '' });

  private prevBrowserSize = this.browserSize();

  constructor() {
    effect(() => {
      const curr = this.browserSize();
      if (!curr) return;

      this.handleBrowserSizeChange(curr);
    });
  }

  ngOnInit(): void {
    this.isSOFLoading.set(false);
    this.initializeRouting();
    this.initializeStores();
    this.initializeResponsiveBehavior();
    this.initializeSOFStream();
  }

  openContactInformationModal(data): void {
    const dialogConfig = {
      autoFocus: true,
      minHeight: this.browserSize() === 'small' ? '315px' : '380px',
      width: this.browserSize() === 'small' ? 'calc(100vw - 20px)' : '650px',
      maxWidth: '650px',
      data: {
        browserSize: this.browserSize(),
        originPage: 'Orders',
        title: data.title,
        contact: data.user,
      },
      disableClose: false,
    };

    this.dialog.open(
      fromSharedComponents.ContactInformationComponent,
      dialogConfig,
    );
  }

  private initializeRouting(): void {
    this.isOrderIdOnRouter = !!this.route.snapshot.paramMap.get('orderId');
    this.isLineItemIdOnRouter =
      !!this.route.snapshot.paramMap.get('lineItemId');
  }

  private initializeStores(): void {
    this.order$ = this.orderStore.select(fromStore.getSelectedOrder);
    this.lineItem$ = this.orderStore.select(fromStore.getSelectedLineItem);
    this.activeOrders$ = this.orderStore.select(fromStore.getActiveOrders);
    this.serviceOrderForm$ = this.orderStore.select(fromStore.getSOFSelected);
  }

  private initializeResponsiveBehavior(): void {
    if (this.browserSize() === 'small') {
      this.onSmallScreen();
    } else {
      this.onLargeScreen();
    }
  }

  private initializeSOFStream(): void {
    this.store.dispatch(new fromStore.LoadServiceOrderForms());

    this.order$
      .pipe(
        filter((o): o is Order => !!o?.Id),
        map((o) => o.Id),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((orderId) => {
        this.orderStore.dispatch(new fromStore.SetSelectedSOF(orderId));
      });

    combineLatest([
      this.order$.pipe(
        filter((o): o is Order => !!o?.Id),
        map((o) => o.Id),
        distinctUntilChanged(),
      ),
      this.serviceOrderForm$.pipe(
        filter(
          (f): f is ContentDocumentLink =>
            !!f?.ContentDocument?.LatestPublishedVersionId,
        ),
        distinctUntilChanged(
          (a, b) =>
            a.ContentDocument?.LatestPublishedVersionId ===
            b.ContentDocument?.LatestPublishedVersionId,
        ),
      ),
    ])
      .pipe(
        tap(() => {
          this.isSOFLoading.set(true);
        }),
        switchMap(([orderId, form]) =>
          this.attachmentService.getSOFBlob(form, orderId).pipe(
            finalize(() => {
              this.isSOFLoading.set(false);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          this.formAPIData.set(data);
        },
        error: () => {
          this.isSOFLoading.set(false);
        },
      });
  }

  private onLargeScreen(): void {
    if (this.isOrderIdOnRouter) {
      this.router.navigateByUrl('/orders');
    }
    this.order$ = this.orderStore.select(fromStore.getSelectedOrder);
    this.lineItem$ = this.orderStore.select(fromStore.getSelectedLineItem);
    this.activeOrders$ = this.orderStore.select(fromStore.getActiveOrders);

    this.activeOrders$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((orders) => {
        if (orders.length > 0) {
          const orderMap = new Map();
          orders.forEach((order) => {
            const count = orderMap.get(order.Status__c) || 0;
            orderMap.set(order.Status__c, count + 1);
          });
          const orderRes = [...orderMap].reduce((acc, curr) => {
            if (acc.length === 0) {
              acc.push({ [curr[0]]: curr[1] });
            } else {
              // eslint-disable-next-line prefer-destructuring
              acc[0][curr[0]] = curr[1];
            }
            return acc;
          }, []);
          // eslint-disable-next-line prefer-destructuring
          this.orderResCurr = orderRes[0];
          this.cdr.detectChanges();
        }
      });

    this.totalActiveLocations$ = this.orderStore.select(
      fromStore.getTotalActiveLocations,
    );
  }

  private onSmallScreen(): void {
    if (this.isOrderIdOnRouter) {
      this.store.dispatch(new fromStore.LoadOrders());
      this.store.dispatch(new fromStore.LoadServiceOrderForms());
      this.order$ = this.orderStore.select(fromStore.getCurrentRouterOrder);

      if (!this.isLineItemIdOnRouter) {
        this.detailView = this.selectedView.order;
        if (this.order()?.Id) {
          this.orderStore.dispatch(
            new fromStore.SetSelectedSOF(this.order()?.Id),
          );
        }
      } else {
        this.detailView = this.selectedView.lineItem;
        this.lineItem$ = this.orderStore.select(
          fromStore.getCurrentRouterLineItem,
        );
      }
      this.cdr.detectChanges();
    }
  }

  private handleBrowserSizeChange(curr: string): void {
    const prev = this.prevBrowserSize;

    if (prev === 'small' && curr !== 'small') {
      this.router.navigateByUrl('/orders');
    }

    this.prevBrowserSize = curr;
  }
}
